// src/components/auditor/AuditorHelp.jsx
// COMPLETE FILE — matches AdminHelpDesk design + mobile-first

import React, { useState } from "react";
import {
  Box, Container, Typography, Paper, Button,
  Stack, TextField, Alert, Divider, Accordion,
  AccordionSummary, AccordionDetails, Chip,
} from "@mui/material";

import ExpandMoreIcon    from "@mui/icons-material/ExpandMore";
import SupportAgentIcon  from "@mui/icons-material/SupportAgent";
import QuizIcon          from "@mui/icons-material/Quiz";
import CheckCircleIcon   from "@mui/icons-material/CheckCircle";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import SwapHorizIcon     from "@mui/icons-material/SwapHoriz";
import PlayArrowIcon     from "@mui/icons-material/PlayArrow";
import EmailIcon         from "@mui/icons-material/Email";

// ─── FAQ Data ─────────────────────────────────────────────────────────────────

const FAQS = [
  {
    q: "How do I start an audit?",
    a: "Go to 'My Dashboard' or 'Active Audits' and tap 'Start Audit' on any initiated audit assigned to you. This opens the asset checklist for that location.",
  },
  {
    q: "What does 'Verified' mean?",
    a: "Mark an asset as Verified when you have physically confirmed it is present at the assigned location and matches the records.",
  },
  {
    q: "What should I do when an asset is missing?",
    a: "Tap 'Missed' for that asset. A support ticket is automatically raised to your admin. Add notes describing the situation — last known location, who may have taken it, etc.",
  },
  {
    q: "When should I use 'Shifted'?",
    a: "Use Shifted when the asset is present but has been moved to a different location from what the records show. A support ticket will be raised so the admin can update the asset's location.",
  },
  {
    q: "Can I change a status after setting it?",
    a: "Yes. Tap the new status button on the asset card at any time before completing the audit. The history log will record both changes.",
  },
  {
    q: "How do I complete an audit?",
    a: "Once all assets have been marked (Verified / Missed / Shifted), a completion dialog appears automatically. Tap 'Complete Audit' to finalise.",
  },
  {
    q: "Can I see the tickets I raised?",
    a: "Not yet in this version. Your admin can see all raised tickets and will update you on resolution.",
  },
];

// ─── Status Guide Row ─────────────────────────────────────────────────────────

const StatusRow = ({ icon, label, chipBg, chipColor, description }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "flex-start",
      gap: 1.5,
      py: 1.25,
      borderBottom: "1px solid #f3f4f6",
      "&:last-child": { borderBottom: "none" },
    }}
  >
    <Chip
      icon={icon}
      label={label}
      size="small"
      sx={{
        bgcolor:    chipBg,
        color:      chipColor,
        fontWeight: 700,
        fontSize:   "0.72rem",
        flexShrink: 0,
        "& .MuiChip-icon": { color: chipColor },
      }}
    />
    <Typography variant="body2" color="text.secondary" sx={{ pt: 0.25 }}>
      {description}
    </Typography>
  </Box>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const AuditorHelp = () => {
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [message,      setMessage]      = useState("");

  const handleSendFeedback = () => {
    if (!message.trim()) return;
    setFeedbackSent(true);
    setMessage("");
    setTimeout(() => setFeedbackSent(false), 5000);
  };

  return (
    <Container
      maxWidth="xl"
      sx={{ mt: 4, mb: 4, display: "flex", flexDirection: "column", gap: 3 }}
    >
      {/* ── HEADER — matches AdminHelpDesk header exactly ── */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box>
          <Stack direction="row" alignItems="center" gap={1.5} mb={0.25}>
            
            <Typography
              variant="h4"
              component="h1"
              sx={{ fontWeight: 700, color: "text.primary", letterSpacing: "-0.5px" }}
            >
              Help & Support
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Guides, FAQs and how to use the audit system
          </Typography>
        </Box>
      </Box>

      {/* ── STATUS GUIDE — Paper outlined like AdminHelpDesk table ── */}
      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
        {/* Section header */}
        <Box
          sx={{
            px: 2.5, py: 2,
            borderBottom: "1px solid #e5e7eb",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <QuizIcon color="primary" fontSize="small" />
          <Typography variant="h6" fontWeight={700} sx={{ fontSize: "1rem" }}>
            Asset Status Guide
          </Typography>
        </Box>

        <Box sx={{ px: 2.5, py: 1 }}>
          <StatusRow
            icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
            label="Verified"
            chipBg="#f0fdf4"
            chipColor="#15803d"
            description="Asset is physically present at the location and matches records. No action required from admin."
          />
          <StatusRow
            icon={<ReportProblemIcon sx={{ fontSize: 14 }} />}
            label="Missed"
            chipBg="#fff1f2"
            chipColor="#b91c1c"
            description="Asset cannot be found at the location. A support ticket is automatically raised to your admin with High priority."
          />
          <StatusRow
            icon={<SwapHorizIcon sx={{ fontSize: 14 }} />}
            label="Shifted"
            chipBg="#fffbeb"
            chipColor="#b45309"
            description="Asset is present but moved to a different location from records. Support ticket raised so admin can update the asset's location."
          />
        </Box>
      </Paper>

      {/* ── AUDIT FLOW — Paper outlined ── */}
      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
        <Box
          sx={{
            px: 2.5, py: 2,
            borderBottom: "1px solid #e5e7eb",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <PlayArrowIcon color="primary" fontSize="small" />
          <Typography variant="h6" fontWeight={700} sx={{ fontSize: "1rem" }}>
            How to Complete an Audit
          </Typography>
        </Box>

        <Box sx={{ px: 2.5, py: 2 }}>
          <Stack spacing={1.5}>
            {[
              "Open 'Active Audits' from the sidebar",
              "Tap 'Start Audit' on an assigned audit event",
              "For each asset in the list, tap Verified, Missed, or Shifted",
              "For Missed / Shifted — add notes to help admin resolve faster",
              "Once all assets are marked, the completion dialog opens automatically",
              "Tap 'Complete Audit' to finalise and return to dashboard",
            ].map((step, i) => (
              <Box
                key={i}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.5,
                  pb: 1.5,
                  borderBottom:
                    i < 5 ? "1px solid #f3f4f6" : "none",
                }}
              >
                <Box
                  sx={{
                    width: 24, height: 24,
                    borderRadius: "50%",
                    bgcolor: "#2563eb",
                    color: "#fff",
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {i + 1}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ pt: 0.25 }}>
                  {step}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      </Paper>

      {/* ── FAQs — Paper outlined ── */}
      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
        <Box
          sx={{
            px: 2.5, py: 2,
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          <Typography variant="h6" fontWeight={700} sx={{ fontSize: "1rem" }}>
            Frequently Asked Questions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {FAQS.length} questions answered
          </Typography>
        </Box>

        {FAQS.map((faq, i) => (
          <Accordion
            key={i}
            disableGutters
            elevation={0}
            sx={{
              borderBottom: i < FAQS.length - 1 ? "1px solid #f3f4f6" : "none",
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon fontSize="small" color="action" />}
              sx={{ px: 2.5, py: 0.5, minHeight: 52 }}
            >
              <Typography variant="body2" fontWeight={600} color="text.primary">
                {faq.q}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2.5, pt: 0, pb: 2 }}>
              <Typography variant="body2" color="text.secondary" lineHeight={1.6}>
                {faq.a}
              </Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Paper>
    </Container>
  );
};

export default AuditorHelp;